import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Check, Zap, Crown, Loader2, AlertCircle } from "lucide-react";
import { useSelector, useDispatch } from "react-redux";
import { redirectToCheckout } from "../../services/stripeService";
import { getUserSubscription } from "../../services/subscriptionService";
import { loginWithGoogle } from "../../app/features/auth";
import Navigation from "../../components/sections/Navigation";
import { Footer } from "../../components";
import Button from "../../components/ui/Button";
import toast from "react-hot-toast";

const Pricing = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { session, user } = useSelector((state) => state.auth);
  const [loading, setLoading] = useState(false);
  const [subscription, setSubscription] = useState(null);
  const [loadingSubscription, setLoadingSubscription] = useState(true);

  useEffect(() => {
    document.documentElement.classList.add("dark");
    loadSubscription();
    return () => {
      document.documentElement.classList.remove("dark");
    };
  }, [user]);

  // Handle hash navigation on page load
  useEffect(() => {
    const hash = window.location.hash.slice(1);
    if (hash) {
      setTimeout(() => {
        const element = document.getElementById(hash);
        if (element) {
          element.scrollIntoView({ behavior: "smooth" });
        }
      }, 300);
    }
  }, []);

  const loadSubscription = async () => {
    if (!user?.id) {
      setLoadingSubscription(false);
      return;
    }

    try {
      const { subscription: sub, error } = await getUserSubscription(user.id);
      if (error) {
        console.error("Error loading subscription:", error);
      } else {
        setSubscription(sub);
      }
    } catch (error) {
      console.error("Exception loading subscription:", error);
    } finally {
      setLoadingSubscription(false);
    }
  };

  const handleSubscribe = async (planType) => {
    if (!session || !user) {
      // User needs to sign in first
      toast.error("Please sign in to subscribe");
      try {
        await dispatch(loginWithGoogle()).unwrap();
      } catch (error) {
        console.error("Error signing in:", error);
        toast.error("Failed to sign in. Please try again.");
      }
      return;
    }

    const priceId =
      planType === "plan_b"
        ? import.meta.env.REACT_APP_STRIPE_PLAN_B_PRICE_ID
        : import.meta.env.REACT_APP_STRIPE_PLAN_C_PRICE_ID;

    if (!priceId) {
      toast.error("Stripe configuration error. Please contact support.");
      return;
    }

    setLoading(true);
    try {
      await redirectToCheckout(priceId, user.id);
      // User will be redirected to Stripe, so we don't need to do anything else
    } catch (error) {
      console.error("Error redirecting to checkout:", error);
      toast.error(
        error?.message || "Failed to start checkout. Please try again."
      );
      setLoading(false);
    }
  };

  const plans = [
    {
      name: "Free",
      planType: "free",
      price: "$0",
      period: "forever",
      description: "Perfect for trying out our service",
      features: [
        "1 analysis per 24 hours",
        "Basic personality insights",
        "Posture analysis",
        "Style recommendations",
        "No credit card required",
      ],
      cta: "Current Plan",
      popular: false,
      icon: <Zap className="w-6 h-6" />,
    },
    {
      name: "Basic",
      planType: "plan_b",
      price: "$4",
      period: "per month",
      description: "Great for regular users",
      features: [
        "5 analyses per 24 hours",
        "All free features",
        "Priority processing",
        "Email support",
        "Cancel anytime",
      ],
      cta: subscription?.plan_type === "plan_b" ? "Current Plan" : "Subscribe",
      popular: true,
      icon: <Crown className="w-6 h-6" />,
    },
    {
      name: "Pro",
      planType: "plan_c",
      price: "$10",
      period: "per month",
      description: "For power users and professionals",
      features: [
        "Unlimited analyses",
        "All Basic features",
        "Fastest processing",
        "Priority support",
        "Advanced insights",
        "Cancel anytime",
      ],
      cta: subscription?.plan_type === "plan_c" ? "Current Plan" : "Subscribe",
      popular: false,
      icon: <Crown className="w-6 h-6" />,
    },
  ];

  const currentPlan = subscription?.plan_type || "free";

  return (
    <div className="min-h-screen bg-stone-950 text-zinc-100 font-sans">
      <Navigation />

      <section className="relative pt-24 sm:pt-32 pb-16 sm:pb-24 px-4 sm:px-6 overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-teal-500/10 rounded-full blur-3xl"></div>
          <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-rose-500/10 rounded-full blur-3xl"></div>
        </div>

        <div className="max-w-7xl mx-auto relative">
          {/* Header */}
          <div className="text-center mb-12 sm:mb-16">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-teal-500/10 via-rose-500/10 to-orange-500/10 border border-teal-500/20 mb-6">
              <div className="w-2 h-2 rounded-full bg-teal-400 animate-pulse"></div>
              <span className="text-xs sm:text-sm font-medium text-teal-400">
                Choose Your Plan
              </span>
            </div>
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold mb-4 bg-gradient-to-r from-zinc-100 via-teal-100 to-rose-100 bg-clip-text text-transparent">
              Simple, Transparent Pricing
            </h1>
            <p className="text-lg sm:text-xl text-zinc-400 max-w-2xl mx-auto">
              Start with our free tier, then upgrade when you need more analyses
            </p>
          </div>

          {/* Pricing Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8 max-w-6xl mx-auto">
            {plans.map((plan) => {
              const isCurrentPlan = currentPlan === plan.planType;
              const isPopular = plan.popular;

              return (
                <div
                  key={plan.planType}
                  className={`relative rounded-2xl p-6 sm:p-8 border transition-all duration-300 ${
                    isPopular
                      ? "border-teal-500/50 bg-gradient-to-br from-teal-950/30 to-stone-900/50 scale-105"
                      : isCurrentPlan
                      ? "border-rose-500/50 bg-gradient-to-br from-rose-950/20 to-stone-900/50"
                      : "border-zinc-800 bg-stone-900/30 hover:border-zinc-700"
                  }`}
                >
                  {isPopular && (
                    <div className="absolute -top-4 left-1/2 -translate-x-1/2 px-4 py-1 rounded-full bg-teal-500 text-white text-xs font-semibold">
                      Most Popular
                    </div>
                  )}

                  {isCurrentPlan && (
                    <div className="absolute -top-4 right-4 px-3 py-1 rounded-full bg-rose-500 text-white text-xs font-semibold">
                      Current
                    </div>
                  )}

                  <div className="mb-6">
                    <div className="flex items-center gap-3 mb-4">
                      <div
                        className={`p-2 rounded-lg ${
                          isPopular
                            ? "bg-teal-500/20 text-teal-400"
                            : "bg-rose-500/20 text-rose-400"
                        }`}
                      >
                        {plan.icon}
                      </div>
                      <h3 className="text-2xl font-bold">{plan.name}</h3>
                    </div>
                    <p className="text-zinc-400 text-sm mb-4">
                      {plan.description}
                    </p>
                    <div className="flex items-baseline gap-2">
                      <span className="text-4xl font-bold">{plan.price}</span>
                      <span className="text-zinc-400">{plan.period}</span>
                    </div>
                  </div>

                  <ul className="space-y-3 mb-8">
                    {plan.features.map((feature, index) => (
                      <li key={index} className="flex items-start gap-3">
                        <Check className="w-5 h-5 text-teal-400 flex-shrink-0 mt-0.5" />
                        <span className="text-zinc-300">{feature}</span>
                      </li>
                    ))}
                  </ul>

                  <Button
                    onClick={() => handleSubscribe(plan.planType)}
                    disabled={isCurrentPlan || loading}
                    className={`w-full ${
                      isCurrentPlan
                        ? "bg-zinc-800 text-zinc-400 cursor-not-allowed"
                        : isPopular
                        ? "bg-gradient-to-r from-teal-500 to-teal-600 hover:from-teal-600 hover:to-teal-700"
                        : "bg-gradient-to-r from-rose-500 to-rose-600 hover:from-rose-600 hover:to-rose-700"
                    }`}
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Processing...
                      </>
                    ) : (
                      plan.cta
                    )}
                  </Button>
                </div>
              );
            })}
          </div>

          {/* Additional Info */}
          <div className="mt-12 text-center">
            <p className="text-zinc-400 text-sm">
              All plans include our full analysis features. Upgrade or cancel
              anytime.
            </p>
            {subscription && subscription.plan_type !== "free" && (
              <p className="text-zinc-400 text-sm mt-2">
                Your current plan:{" "}
                <span className="text-teal-400 font-semibold">
                  {subscription.plan_type === "plan_b" ? "Basic" : "Pro"}
                </span>
              </p>
            )}
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
};

export default Pricing;
